import cx from 'classnames';
import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Typography } from '../../../design-system/components/typography/Typography';
import { useWemap } from '../../../design-system/components/wemap/hooks/useWemap';
import { WemapPinpoint } from '../../../design-system/components/wemap/types/Wemap';
import { boundsFromBoundingBox } from '../../../design-system/components/wemap/utilities/boundsFromBoundingBox';
import { WemapDrawerDefaultContent } from '../../../design-system/components/wemap/WemapDrawerDefaultContent';
import { WemapReact } from '../../../design-system/components/wemap/WemapReact';
import {
    Destination,
    DestinationTypeRegion,
} from '../../../resources/destination/domain/types/destination';
import { formatPointOfInterestIntoWeMapPinPoint } from '../../../resources/pointOfInterest/domain/operations/formatPlaceForMap';
import { getOnlinePointsOfInterestWithDescription } from '../../../resources/pointOfInterest/domain/operations/getPointsOfInterestWithContent';
import { keepUniquePointsOfInterest } from '../../../resources/pointOfInterest/domain/operations/getUniquePointsOfInterest';
import { PointOfInterest } from '../../../resources/pointOfInterest/domain/types/PointOfInterest';
import {
    formatDestinationRegionAsWemapPinpoint,
    formatRegionAsWemapPinpoint,
} from '../../../resources/region/domain/operations/formatRegionAsMapRegion';
import { Region } from '../../../resources/region/domain/types/region';
import { SecondaryRegionListSection } from '../../../shared/components/SecondaryRegionListSection/SecondaryRegionListSection';
import { useDestinationBoundaries } from '../../../shared/hooks/useDestinationBoundaries';
import { useIntersectOnce } from '../../../shared/hooks/useIntersectOnce';
import { useLocale } from '../../../shared/hooks/useLocale';
import { useRegionBoundaries } from '../../../shared/hooks/useRegionBoundaries';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { IntrinsicElement } from '../../../shared/utilities/types/IntrinsicElement';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import { WhereToGoSectionMapPanel } from './WhereToGoSectionMapPanel';

import style from './WhereToGoSection.module.css';

type WhereToGoSectionProps<R, DR, POI> = {
    destination: PickDomain<
        Destination,
        'id' | 'name' | 'preposition' | 'slug',
        {
            geolocation: Destination['geolocation'] | null;
            parentDestination: PickDomain<
                Destination,
                'id' | 'slug' | 'preposition' | 'name'
            > | null;
        }
    >;
    pointsOfInterest: Array<POI>;
    regions: Array<R>;
    destinationsTypeRegion: Array<DR>;
    secondaryRegionTitleElement?: IntrinsicElement;
    regionNameElement?: IntrinsicElement;
};

export function WhereToGoSection<
    R extends PickDomain<
        Region,
        'id' | 'name' | 'slug' | 'isHighlighted' | 'description' | 'referentialRegionId',
        {
            coverPicture: Region['coverPicture'] | null;
            destination: PickDomain<Region['destination'], 'id' | 'slug' | 'name' | 'preposition'>;
        }
    >,
    DR extends PickDomain<
        DestinationTypeRegion,
        'id' | 'slug' | 'name' | 'coverPicture',
        {
            description: DestinationTypeRegion['description'] | null;
        }
    >,
    POI extends PickDomain<
        PointOfInterest,
        'id' | 'slug' | 'name' | 'isOnline' | 'geolocation',
        {
            destination: PickDomain<Destination, 'slug'>;
            averageRating: PointOfInterest['averageRating'] | null;
            picture: PointOfInterest['picture'] | null;
            geonamesId: PointOfInterest['geonamesId'] | null;
            description: PointOfInterest['description'] | null;
        }
    >,
>({
    pointsOfInterest,
    destination,
    regions,
    destinationsTypeRegion,
    secondaryRegionTitleElement,
    regionNameElement,
}: WhereToGoSectionProps<R, DR, POI>) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const { locale } = useLocale();
    const { openedPinpointId: openedPinpoint, setOpenedPinpointId: setOpenedPinpoint } = useWemap();
    const { generateUrl } = useUrlGenerator();

    const sectionRef = useRef<HTMLElement>(null);

    const hasUserScrolledCloseToMapSection = useIntersectOnce(sectionRef, {
        root: null,
        rootMargin: '300px',
        threshold: 0,
    });

    const uniquePointsOfInterest = keepUniquePointsOfInterest(
        getOnlinePointsOfInterestWithDescription(pointsOfInterest),
    );

    const pointsOfInterestPinpoints = uniquePointsOfInterest.map((pointOfInterest) =>
        formatPointOfInterestIntoWeMapPinPoint(pointOfInterest),
    );

    const regionBoundaries = useRegionBoundaries();
    const destinationBoundaries = useDestinationBoundaries();

    const highlightedRegions = regions.filter((region) => {
        return region.isHighlighted;
    });

    const secondaryRegions = regions.filter((region) => {
        return !region.isHighlighted;
    });

    const mapRegion: WemapPinpoint | null = useMemo(() => {
        if (!openedPinpoint) {
            return null;
        }

        const region = highlightedRegions.find((r) => r.id === openedPinpoint);
        if (region) {
            const regionBoundariesData = regionBoundaries.getById(region.id);
            if (regionBoundariesData) {
                return formatRegionAsWemapPinpoint(region, regionBoundariesData);
            }
        }

        const destinationRegion = destinationsTypeRegion.find((dr) => dr.id === openedPinpoint);
        if (destinationRegion) {
            const destinationBoundariesData = destinationBoundaries.getById(destinationRegion.id);
            if (destinationBoundariesData) {
                return formatDestinationRegionAsWemapPinpoint(
                    destinationRegion,
                    destinationBoundariesData,
                );
            }
        }

        return null;
    }, [
        openedPinpoint,
        highlightedRegions,
        destinationsTypeRegion,
        regionBoundaries,
        destinationBoundaries,
    ]);

    const mapPinPoints: WemapPinpoint[] = useMemo(() => {
        const points: WemapPinpoint[] = [];
        if (mapRegion) {
            points.push(mapRegion);
        }
        if (pointsOfInterestPinpoints.length > 0) {
            points.push(...pointsOfInterestPinpoints);
        }
        return points;
    }, [mapRegion, pointsOfInterestPinpoints]);

    const boundingBox = destination.geolocation?.boundingBox;
    const bounds = boundingBox ? boundsFromBoundingBox(boundingBox) : undefined;

    const renderDrawer = useCallback(
        ({ openedPinpointId }: { openedPinpointId: number | null }) => {
            const poi = uniquePointsOfInterest.find((p) => p.id === openedPinpointId);
            if (poi) {
                return (
                    <WemapDrawerDefaultContent
                        title={poi.name}
                        description={poi.description}
                        averageRating={poi.averageRating}
                        picture={poi.picture}
                        cta={{
                            label: formatMessage({ id: 'global.cta.discoverPointOfInterest' }),
                            url: generateUrl('place', {
                                destinationSlug: poi.destination.slug,
                                placeId: poi.id,
                                placeSlug: poi.slug,
                            }),
                        }}
                        onClickCta={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.poiClicked({
                                    ...defaultProperties,
                                    block: 'where_to_go',
                                    card_number: 0,
                                    click_type: 'button',
                                });
                            });
                        }}
                    />
                );
            }

            const region = highlightedRegions.find((r) => r.id === openedPinpointId);
            if (region) {
                return (
                    <WemapDrawerDefaultContent
                        title={region.name}
                        description={region.description}
                        averageRating={null}
                        picture={region.coverPicture}
                        cta={{
                            label: formatMessage({ id: 'global.cta.discoverRegion' }),
                            url: generateUrl('region', {
                                destinationSlug: region.destination.slug,
                                regionSlug: region.slug,
                            }),
                        }}
                        onClickCta={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.regionClicked({
                                    ...defaultProperties,
                                    block: 'where_to_go',
                                    click_type: 'button',
                                    card_number: 0,
                                    destination_id: region.destination.id,
                                    destination_slug: region.destination.slug,
                                    region_id: region.id,
                                    region_slug: region.slug,
                                    region_uuid: region.referentialRegionId ?? '',
                                });
                            });
                        }}
                    />
                );
            }

            const destinationRegion = destinationsTypeRegion.find(
                (dr) => dr.id === openedPinpointId,
            );
            if (destinationRegion) {
                return (
                    <WemapDrawerDefaultContent
                        title={destinationRegion.name}
                        description={destinationRegion.description}
                        averageRating={null}
                        picture={destinationRegion.coverPicture}
                        cta={{
                            label: formatMessage({ id: 'global.cta.discoverRegion' }),
                            url: generateUrl('destination', {
                                destinationSlug: destinationRegion.slug,
                            }),
                        }}
                        onClickCta={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.destinationClicked({
                                    ...defaultProperties,
                                    block: 'where_to_go',
                                    card_number: 0,
                                    destination_id: destinationRegion.id,
                                    destination_slug: destinationRegion.slug,
                                    tool_counter: 0,
                                });
                            });
                        }}
                    />
                );
            }

            return null;
        },
        [
            uniquePointsOfInterest,
            highlightedRegions,
            destinationsTypeRegion,
            formatMessage,
            generateUrl,
            trackEvent,
        ],
    );

    const selectedRegion:
        | {
              type: 'destinationRegion';
              item: DR;
          }
        | { type: 'region'; item: R }
        | null = useMemo(() => {
        if (!openedPinpoint) {
            return null;
        }

        const region = highlightedRegions.find((r) => r.id === openedPinpoint);
        if (region) {
            return { type: 'region', item: region };
        }

        const destinationRegion = destinationsTypeRegion.find((dr) => dr.id === openedPinpoint);
        if (destinationRegion) {
            return { type: 'destinationRegion', item: destinationRegion };
        }

        return null;
    }, [openedPinpoint, highlightedRegions, destinationsTypeRegion]);

    function handleRegionSelect(region: { type: 'region'; item: R }) {
        setOpenedPinpoint(region.item.id);
        void regionBoundaries.fetch(region.item, locale);
    }

    function handleDestinationRegionSelect(destinationRegion: {
        type: 'destinationRegion';
        item: DR;
    }) {
        setOpenedPinpoint(destinationRegion.item.id);
        void destinationBoundaries.fetch(destinationRegion.item, locale);
    }

    return (
        <>
            <section className={cx('main-container hidden-xs hidden-sm hidden-md', style.title)}>
                <Typography as="div" scale="secondary-m-bold">
                    {formatMessage({ id: 'page.whereToGo.map.title' })}
                </Typography>
            </section>

            {bounds ? (
                <section
                    ref={sectionRef}
                    className="main-container-cover hidden-xs hidden-sm hidden-md"
                >
                    <div className={style.mapContainer}>
                        {destinationsTypeRegion.length > 0 || highlightedRegions.length > 0 ? (
                            <WhereToGoSectionMapPanel
                                destinationsTypeRegion={destinationsTypeRegion}
                                regions={highlightedRegions}
                                selectedRegion={selectedRegion}
                                onRegionSelect={handleRegionSelect}
                                onDestinationRegionSelect={handleDestinationRegionSelect}
                            />
                        ) : null}
                        <div className={style.mapWrapper}>
                            {hasUserScrolledCloseToMapSection ? (
                                <WemapReact
                                    bounds={bounds}
                                    pinpoints={mapPinPoints}
                                    isDrawerClosable={true}
                                    isDrawerOpen={Boolean(openedPinpoint)}
                                    renderDrawer={renderDrawer}
                                    trackingInformation={{
                                        placeClicked: {
                                            block: 'where_to_go',
                                            card_number: 0,
                                            click_type: 'button',
                                        },
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </section>
            ) : null}

            {secondaryRegions.length > 0 ? (
                <section className="main-container">
                    <SecondaryRegionListSection
                        destination={
                            destination.parentDestination
                                ? destination.parentDestination
                                : destination
                        }
                        regions={secondaryRegions}
                        titleElement={secondaryRegionTitleElement}
                        regionNameElement={regionNameElement}
                    />
                </section>
            ) : null}
        </>
    );
}
